import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { CheckCircle2, Phone, ArrowRight, Calendar, Clock } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";

const ThankYouPage = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const service = searchParams.get("service") || "consultation";
  const name = searchParams.get("name") || "";

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getServiceMessage = () => {
    switch (service) {
      case "retirement-planning":
        return "retirement planning consultation";
      case "wealth-management":
        return "wealth management strategy session";
      case "fiduciary-advisor":
        return "fiduciary consultation";
      case "financial-advisor":
        return "financial planning consultation";
      case "contact-page":
        return "consultation";
      default:
        return "consultation";
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section
        className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#f8fafb] via-white to-[#e8eef2]"
        data-testid="thank-you-hero"
      >
        <div className="max-w-3xl mx-auto text-center">
          {/* Success Icon */}
          <div className="mb-8 inline-flex items-center justify-center w-24 h-24 bg-[#4a6274] rounded-full shadow-lg animate-bounce-slow">
            <CheckCircle2 className="w-12 h-12 text-white" />
          </div>

          <h1
            className="text-4xl sm:text-5xl font-bold text-[#3d4f5f] mb-6 leading-tight"
            data-testid="thank-you-title"
          >
            Thank You{name ? `, ${name.split(" ")[0]}` : ""}!
          </h1>

          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Your request for a free {getServiceMessage()} has been received. One
            of our experienced advisors will contact you within{" "}
            <strong>24 business hours</strong>.
          </p>

          {/* What Happens Next */}
          <Card
            className="p-8 bg-white shadow-xl border border-gray-100 text-left mb-10"
            data-testid="next-steps-card"
          >
            <h2 className="text-2xl font-bold text-[#3d4f5f] mb-6 text-center">
              What Happens Next?
            </h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#e8eef2] rounded-full flex items-center justify-center">
                  <span className="text-[#3d4f5f] font-bold">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-[#3d4f5f] mb-1">
                    We Review Your Information
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Our team will review your request and match you with the
                    right advisor for your needs.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#e8eef2] rounded-full flex items-center justify-center">
                  <span className="text-[#3d4f5f] font-bold">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-[#3d4f5f] mb-1">
                    We Reach Out to Schedule
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Expect a call or email within 24 hours to schedule your
                    no-cost consultation at a time that works for you.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#e8eef2] rounded-full flex items-center justify-center">
                  <span className="text-[#3d4f5f] font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-[#3d4f5f] mb-1">
                    Free Discovery Meeting
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Meet with your advisor to discuss your goals, ask questions,
                    and explore how we can help—no obligation.
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Quick Info Cards */}
          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            <Card className="p-6 bg-[#3d4f5f] text-white">
              <Clock className="w-8 h-8 mb-3 mx-auto" />
              <h3 className="font-semibold mb-1">Response Time</h3>
              <p className="text-sm text-gray-200">Within 24 business hours</p>
            </Card>
            <Card className="p-6 bg-[#4a6274] text-white">
              <Calendar className="w-8 h-8 mb-3 mx-auto" />
              <h3 className="font-semibold mb-1">Consultation</h3>
              <p className="text-sm text-gray-200">Free, no obligation</p>
            </Card>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button
                variant="outline"
                className="border-2 border-[#3d4f5f] text-[#3d4f5f] px-8 py-6 text-base rounded-lg hover:bg-[#f8fafb] transition-all duration-200 w-full sm:w-auto"
                data-testid="back-to-home-btn"
              >
                Back to Home
              </Button>
            </Link>
            <a href="tel:+19184777227">
              <Button
                className="bg-[#3d4f5f] hover:bg-[#2d3e4e] text-white px-8 py-6 text-base rounded-lg shadow-lg w-full sm:w-auto"
                data-testid="call-now-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now: 918.477.7227
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section
        className="py-16 px-4 sm:px-6 lg:px-8 bg-white"
        data-testid="resources-section"
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-[#3d4f5f] mb-8 text-center">
            While You Wait, Explore Our Services
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { href: "/retirement-planning", label: "Retirement Planning" },
              { href: "/wealth-management", label: "Wealth Management" },
              { href: "/financial-advisor", label: "Financial Advisor" },
              { href: "/fiduciary-advisor", label: "Fiduciary Advisor" },
            ].map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="group p-4 border border-gray-200 rounded-lg hover:border-[#3d4f5f] hover:shadow-md transition-all duration-200"
              >
                <span className="text-sm font-medium text-gray-700 group-hover:text-[#3d4f5f] flex items-center justify-between">
                  {link.label}
                  <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ThankYouPage;
